<template>
    <div class="role-permission space-y-6">
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <div class="role-toolbar">
                    <div class="role-toolbar__copy">
                        <h2 class="card-title text-lg font-semibold">{{ $tt('用户与角色') }}</h2>
                        <p class="role-toolbar__summary">{{ $tt('管理收藏家与听众角色') }}</p>
                    </div>
                    <div class="role-toolbar__actions">
                        <label class="role-search">
                            <svg-icon name="filter" class="h-4 w-4 shrink-0 fill-current opacity-50" />
                            <input
                                v-model.trim="searchKeyword"
                                type="search"
                                :placeholder="$tt('搜索用户名或邮箱')"
                                :aria-label="$tt('搜索用户名或邮箱')"
                            >
                        </label>
                        <button type="button" class="btn btn-primary btn-sm gap-2 whitespace-nowrap" @click="openCreateModal">
                            <svg-icon name="add" class="h-4 w-4 fill-current" />
                            {{ $tt('新增用户') }}
                        </button>
                    </div>
                </div>

                <div class="role-summary" aria-live="polite">
                    <span>{{ $tt('共 {count} 位用户', { count: users.length }) }}</span>
                    <span class="role-summary__divider" aria-hidden="true"></span>
                    <span>{{ $tt('{count} 位收藏家', { count: adminCount }) }}</span>
                    <span class="role-summary__divider" aria-hidden="true"></span>
                    <span>{{ $tt('{count} 位听众', { count: employeeCount }) }}</span>
                </div>

                <div class="role-table-shell">
                    <div v-if="loading" class="role-loading" aria-live="polite">
                        <div v-for="index in 6" :key="index" class="role-loading__row">
                            <span class="role-skeleton role-skeleton--avatar"></span>
                            <span class="role-skeleton role-skeleton--name"></span>
                            <span class="role-skeleton role-skeleton--email"></span>
                            <span class="role-skeleton role-skeleton--select"></span>
                        </div>
                    </div>

                    <div v-else-if="errorMessage" class="role-state">
                        <svg-icon name="close" class="h-6 w-6 fill-current text-warning" />
                        <div>
                            <p class="font-medium">{{ $tt('用户列表加载失败') }}</p>
                            <p class="mt-1 text-sm opacity-60">{{ errorMessage }}</p>
                        </div>
                        <button type="button" class="btn btn-sm btn-outline" @click="loadRoleUsers">
                            {{ $tt('重试') }}
                        </button>
                    </div>

                    <div v-else-if="!filteredUsers.length" class="role-state">
                        <svg-icon name="user" class="h-7 w-7 fill-current opacity-40" />
                        <div>
                            <p class="font-medium">{{ searchKeyword ? $tt('没有匹配的用户') : $tt('暂无用户') }}</p>
                            <p v-if="searchKeyword" class="mt-1 text-sm opacity-60">{{ $tt('请尝试其他搜索关键词') }}</p>
                        </div>
                    </div>

                    <div v-else class="overflow-x-auto">
                        <table class="role-table">
                            <thead>
                                <tr>
                                    <th>{{ $tt('用户') }}</th>
                                    <th>{{ $tt('邮箱') }}</th>
                                    <th>{{ $tt('角色') }}</th>
                                    <th>{{ $tt('Subsonic') }}</th>
                                    <th>{{ $tt('账号状态') }}</th>
                                    <th class="role-actions-column">{{ $tt('操作') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="user in pagedUsers" :key="user.id">
                                    <td>
                                        <div class="role-user">
                                            <div class="avatar placeholder">
                                                <div class="role-user__avatar">
                                                    <span>{{ userInitial(user) }}</span>
                                                </div>
                                            </div>
                                            <div class="min-w-0">
                                                <div class="truncate font-medium">{{ user.name }}</div>
                                                <div class="mt-0.5 text-xs opacity-50">ID {{ user.id }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="role-email">{{ user.email || '-' }}</td>
                                    <td>
                                        <select
                                            class="select select-bordered select-sm role-select"
                                            :value="user.is_superuser ? 'admin' : 'employee'"
                                            :disabled="savingUserId !== null"
                                            :aria-label="$tt('设置 {name} 的角色', { name: user.name })"
                                            @change="changeRole(user, $event)"
                                        >
                                            <option value="admin">{{ $tt('收藏家') }}</option>
                                            <option value="employee">{{ $tt('听众') }}</option>
                                        </select>
                                    </td>
                                    <td>
                                        <span :class="['role-status', user.subsonic_enabled ? 'is-enabled' : 'is-muted']">
                                            {{ user.subsonic_enabled ? $tt('已启用') : $tt('未启用') }}
                                        </span>
                                    </td>
                                    <td>
                                        <span :class="['role-status', user.is_active ? 'is-enabled' : 'is-disabled']">
                                            {{ user.is_active ? $tt('正常') : $tt('已停用') }}
                                        </span>
                                    </td>
                                    <td class="role-actions-column">
                                        <button type="button" class="btn btn-ghost btn-sm btn-square" :aria-label="$tt('编辑 {name}', { name: user.name })" @click="openEditModal(user)">
                                            <svg-icon name="edit" class="h-4 w-4 fill-current" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="filteredUsers.length > pageSize" class="role-pagination">
                    <span class="text-sm opacity-60">
                        {{ $tt('第 {current} / {total} 页', { current: currentPage, total: totalPages }) }}
                    </span>
                    <div class="join">
                        <button type="button" class="btn btn-sm join-item" :disabled="currentPage === 1" :aria-label="$tt('上一页')" @click="currentPage -= 1">
                            <svg-icon name="reverse" class="h-4 w-4 fill-current" />
                        </button>
                        <button type="button" class="btn btn-sm join-item" :disabled="currentPage === totalPages" :aria-label="$tt('下一页')" @click="currentPage += 1">
                            <svg-icon name="reverse" class="h-4 w-4 rotate-180 fill-current" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showCreateModal" class="modal modal-open" @keydown.esc="closeCreateModal">
            <div class="modal-box w-11/12 max-w-md">
                <div class="flex items-start justify-between gap-4">
                    <div>
                        <h3 class="text-lg font-semibold">{{ $tt('新增用户') }}</h3>
                        <p class="mt-1 text-sm text-base-content/60">{{ $tt('创建可登录系统的新用户') }}</p>
                    </div>
                    <button type="button" class="btn btn-ghost btn-sm btn-square" :aria-label="$tt('关闭')" :disabled="creatingUser" @click="closeCreateModal">
                        <svg-icon name="close" class="h-4 w-4 fill-current" />
                    </button>
                </div>

                <form class="mt-5 space-y-4" @submit.prevent="createUser">
                    <div class="form-control">
                        <label class="label" for="role-user-username"><span class="label-text font-medium">{{ $tt('用户名') }}</span></label>
                        <input id="role-user-username" v-model.trim="createForm.username" class="input input-bordered focus:input-primary" type="text" maxlength="150" autocomplete="off" required>
                    </div>
                    <div class="form-control">
                        <label class="label" for="role-user-email"><span class="label-text font-medium">{{ $tt('邮箱') }}</span></label>
                        <input id="role-user-email" v-model.trim="createForm.email" class="input input-bordered focus:input-primary" type="email" autocomplete="email">
                    </div>
                    <div class="form-control">
                        <label class="label" for="role-user-password"><span class="label-text font-medium">{{ $tt('初始密码') }}</span></label>
                        <input id="role-user-password" v-model="createForm.password" class="input input-bordered focus:input-primary" type="password" autocomplete="new-password" required>
                    </div>
                    <div class="form-control">
                        <label class="label" for="role-user-role"><span class="label-text font-medium">{{ $tt('角色') }}</span></label>
                        <select id="role-user-role" v-model="createForm.role" class="select select-bordered focus:select-primary">
                            <option value="employee">{{ $tt('听众') }}</option>
                            <option value="admin">{{ $tt('收藏家') }}</option>
                        </select>
                    </div>
                    <p v-if="createError" class="text-sm text-error" role="alert">{{ createError }}</p>
                    <div class="modal-action">
                        <button type="button" class="btn btn-ghost" :disabled="creatingUser" @click="closeCreateModal">{{ $tt('取消') }}</button>
                        <button type="submit" class="btn btn-primary" :disabled="creatingUser || !createForm.username || !createForm.password">
                            <span v-if="creatingUser" class="loading loading-spinner loading-sm"></span>
                            {{ creatingUser ? $tt('创建中') : $tt('创建用户') }}
                        </button>
                    </div>
                </form>
            </div>
            <button type="button" class="modal-backdrop" :aria-label="$tt('关闭')" @click="closeCreateModal"></button>
        </div>

        <div v-if="showEditModal" class="modal modal-open" @keydown.esc="closeEditModal">
            <div class="modal-box w-11/12 max-w-md">
                <div class="flex items-start justify-between gap-4">
                    <div>
                        <h3 class="text-lg font-semibold">{{ $tt('编辑用户') }}</h3>
                        <p class="mt-1 text-sm text-base-content/60">{{ $tt('修改账号资料、角色与登录状态') }}</p>
                    </div>
                    <button type="button" class="btn btn-ghost btn-sm btn-square" :aria-label="$tt('关闭')" :disabled="updatingUser" @click="closeEditModal">
                        <svg-icon name="close" class="h-4 w-4 fill-current" />
                    </button>
                </div>

                <form class="mt-5 space-y-4" @submit.prevent="updateUser">
                    <div class="form-control">
                        <label class="label" for="edit-role-user-username"><span class="label-text font-medium">{{ $tt('用户名') }}</span></label>
                        <input id="edit-role-user-username" v-model.trim="editForm.username" class="input input-bordered focus:input-primary" type="text" maxlength="150" autocomplete="off" required>
                    </div>
                    <div class="form-control">
                        <label class="label" for="edit-role-user-email"><span class="label-text font-medium">{{ $tt('邮箱') }}</span></label>
                        <input id="edit-role-user-email" v-model.trim="editForm.email" class="input input-bordered focus:input-primary" type="email" autocomplete="email">
                    </div>
                    <div class="form-control">
                        <label class="label" for="edit-role-user-password"><span class="label-text font-medium">{{ $tt('重置密码') }}</span></label>
                        <input id="edit-role-user-password" v-model="editForm.password" class="input input-bordered focus:input-primary" type="password" autocomplete="new-password" :placeholder="$tt('留空则不修改密码')">
                    </div>
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div class="form-control">
                            <label class="label" for="edit-role-user-role"><span class="label-text font-medium">{{ $tt('角色') }}</span></label>
                            <select id="edit-role-user-role" v-model="editForm.role" class="select select-bordered focus:select-primary">
                                <option value="employee">{{ $tt('听众') }}</option>
                                <option value="admin">{{ $tt('收藏家') }}</option>
                            </select>
                        </div>
                        <div class="form-control">
                            <span class="label"><span class="label-text font-medium">{{ $tt('账号状态') }}</span></span>
                            <label class="label h-12 cursor-pointer justify-start gap-3 rounded-md border border-base-300 px-3">
                                <input v-model="editForm.is_active" type="checkbox" class="toggle toggle-primary toggle-sm">
                                <span class="label-text">{{ editForm.is_active ? $tt('正常') : $tt('已停用') }}</span>
                            </label>
                        </div>
                    </div>
                    <p v-if="editError" class="text-sm text-error" role="alert">{{ editError }}</p>
                    <div class="modal-action">
                        <button type="button" class="btn btn-ghost" :disabled="updatingUser" @click="closeEditModal">{{ $tt('取消') }}</button>
                        <button type="submit" class="btn btn-primary" :disabled="updatingUser || !editForm.username">
                            <span v-if="updatingUser" class="loading loading-spinner loading-sm"></span>
                            {{ updatingUser ? $tt('保存中') : $tt('保存修改') }}
                        </button>
                    </div>
                </form>
            </div>
            <button type="button" class="modal-backdrop" :aria-label="$tt('关闭')" @click="closeEditModal"></button>
        </div>
    </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'

export default {
    name: 'RolePermission',
    components: { SvgIcon },
    data() {
        return {
            users: [],
            searchKeyword: '',
            loading: true,
            errorMessage: '',
            savingUserId: null,
            currentPage: 1,
            pageSize: 20,
            showCreateModal: false,
            creatingUser: false,
            createError: '',
            createForm: {
                username: '',
                email: '',
                password: '',
                role: 'employee'
            },
            showEditModal: false,
            updatingUser: false,
            editError: '',
            editForm: {
                user_id: null,
                username: '',
                email: '',
                password: '',
                role: 'employee',
                is_active: true
            }
        }
    },
    computed: {
        filteredUsers() {
            const keyword = this.searchKeyword.toLowerCase()
            if (!keyword) return this.users
            return this.users.filter(user => {
                return String(user.name || '').toLowerCase().includes(keyword) ||
                    String(user.email || '').toLowerCase().includes(keyword)
            })
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filteredUsers.length / this.pageSize))
        },
        pagedUsers() {
            const start = (this.currentPage - 1) * this.pageSize
            return this.filteredUsers.slice(start, start + this.pageSize)
        },
        adminCount() {
            return this.users.filter(user => user.is_superuser).length
        },
        employeeCount() {
            return this.users.length - this.adminCount
        }
    },
    watch: {
        searchKeyword() {
            this.currentPage = 1
        },
        totalPages(value) {
            if (this.currentPage > value) this.currentPage = value
        }
    },
    mounted() {
        this.loadRoleUsers()
    },
    methods: {
        loadRoleUsers() {
            this.loading = true
            this.errorMessage = ''
            this.$api.Task.roleList().then(res => {
                const admins = (res.data && res.data.superusers) || []
                const employees = (res.data && res.data.employees) || []
                this.users = [
                    ...admins.map(user => ({ ...user, is_superuser: true })),
                    ...employees.map(user => ({ ...user, is_superuser: false }))
                ].sort((left, right) => left.id - right.id)
            }).catch(error => {
                this.errorMessage = error && error.message ? error.message : this.$tt('请求失败')
            }).finally(() => {
                this.loading = false
            })
        },
        userInitial(user) {
            return String(user.name || '?').trim().slice(0, 1).toUpperCase()
        },
        openCreateModal() {
            this.createForm = { username: '', email: '', password: '', role: 'employee' }
            this.createError = ''
            this.showCreateModal = true
            this.$nextTick(() => document.getElementById('role-user-username')?.focus())
        },
        closeCreateModal() {
            if (this.creatingUser) return
            this.showCreateModal = false
        },
        responseErrorMessage(response) {
            if (response && response.message) return response.message
            const data = response && response.error && response.error.response && response.error.response.data
            if (!data || typeof data !== 'object') return this.$tt('创建失败，请检查输入内容')
            const firstError = Object.values(data)[0]
            return Array.isArray(firstError) ? firstError[0] : String(firstError)
        },
        createUser() {
            if (this.creatingUser) return
            this.creatingUser = true
            this.createError = ''
            this.$api.Task.createRoleUser(this.createForm).then(res => {
                if (!res.result) throw new Error(this.responseErrorMessage(res))
                this.showCreateModal = false
                this.$myMsg.notify({
                    title: this.$tt('用户已创建'),
                    type: 'success',
                    time: 1500
                })
                this.loadRoleUsers()
            }).catch(error => {
                this.createError = error && error.message ? error.message : this.$tt('创建失败，请检查输入内容')
            }).finally(() => {
                this.creatingUser = false
            })
        },
        openEditModal(user) {
            this.editForm = {
                user_id: user.id,
                username: user.name,
                email: user.email || '',
                password: '',
                role: user.is_superuser ? 'admin' : 'employee',
                is_active: user.is_active
            }
            this.editError = ''
            this.showEditModal = true
            this.$nextTick(() => document.getElementById('edit-role-user-username')?.focus())
        },
        closeEditModal() {
            if (this.updatingUser) return
            this.showEditModal = false
        },
        updateUser() {
            if (this.updatingUser) return
            this.updatingUser = true
            this.editError = ''
            this.$api.Task.updateRoleUser(this.editForm).then(res => {
                if (!res.result) throw new Error(this.responseErrorMessage(res))
                this.showEditModal = false
                this.$myMsg.notify({
                    title: this.$tt('用户信息已更新'),
                    type: 'success',
                    time: 1500
                })
                this.loadRoleUsers()
            }).catch(error => {
                this.editError = error && error.message ? error.message : this.$tt('更新失败，请检查输入内容')
            }).finally(() => {
                this.updatingUser = false
            })
        },
        changeRole(user, event) {
            const role = event.target.value
            const makeAdmin = role === 'admin'
            if (user.is_superuser === makeAdmin) return
            if (!makeAdmin && this.adminCount <= 1) {
                this.$myMsg.notify({
                    title: this.$tt('至少保留一位收藏家'),
                    type: 'warning',
                    time: 2000
                })
                event.target.value = 'admin'
                return
            }

            const previousRole = user.is_superuser
            user.is_superuser = makeAdmin
            this.savingUserId = user.id
            this.$api.Task.updateRoleUsers({
                admin_users: this.users.filter(item => item.is_superuser).map(item => item.id),
                employee_users: this.users.filter(item => !item.is_superuser).map(item => item.id)
            }).then(res => {
                if (!res.result) throw new Error(res.message || this.$tt('更新失败'))
                this.$myMsg.notify({
                    title: this.$tt('角色已更新'),
                    type: 'success',
                    time: 1200
                })
            }).catch(error => {
                user.is_superuser = previousRole
                this.$forceUpdate()
                this.$myMsg.notify({
                    title: this.$tt('更新失败'),
                    content: error && error.message ? error.message : '',
                    type: 'error',
                    time: 2000
                })
            }).finally(() => {
                this.savingUserId = null
            })
        }
    }
}
</script>

<style scoped>
.role-permission {
    width: 100%;
}

.role-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
}

.role-toolbar__copy {
    min-width: 0;
}

.role-toolbar__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.role-toolbar__summary {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    opacity: 0.6;
}

.role-search {
    display: flex;
    width: min(20rem, 100%);
    height: 2.5rem;
    align-items: center;
    gap: 0.625rem;
    border: 1px solid hsl(var(--bc) / 0.2);
    border-radius: 0.375rem;
    background: hsl(var(--b1));
    padding: 0 0.75rem;
}

.role-search:focus-within {
    border-color: hsl(var(--p));
    outline: 2px solid hsl(var(--p) / 0.16);
    outline-offset: 1px;
}

.role-search input {
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    outline: 0;
    font-size: 0.875rem;
}

.role-summary {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-top: 1.25rem;
    color: hsl(var(--bc) / 0.6);
    font-size: 0.75rem;
}

.role-summary__divider {
    width: 1px;
    height: 0.75rem;
    background: hsl(var(--bc) / 0.18);
}

.role-table-shell {
    min-height: 22rem;
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid hsl(var(--bc) / 0.14);
    border-radius: 0.375rem;
    background: hsl(var(--b1));
}

.role-table {
    width: 100%;
    min-width: 48rem;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.role-table th {
    height: 2.75rem;
    padding: 0 1rem;
    background: hsl(var(--b2));
    color: hsl(var(--bc) / 0.58);
    font-size: 0.75rem;
    font-weight: 600;
    text-align: left;
}

.role-table td {
    height: 4rem;
    padding: 0.625rem 1rem;
    border-top: 1px solid hsl(var(--bc) / 0.1);
    vertical-align: middle;
}

.role-table tbody tr:hover {
    background: hsl(var(--b2) / 0.55);
}

.role-actions-column {
    width: 4rem;
    text-align: center !important;
}

.role-user {
    display: flex;
    min-width: 11rem;
    align-items: center;
    gap: 0.75rem;
}

.role-user__avatar {
    display: grid;
    width: 2rem;
    height: 2rem;
    place-items: center;
    border-radius: 50%;
    background: hsl(var(--p) / 0.12);
    color: hsl(var(--p));
    font-size: 0.75rem;
    font-weight: 700;
}

.role-email {
    max-width: 18rem;
    overflow: hidden;
    color: hsl(var(--bc) / 0.68);
    text-overflow: ellipsis;
    white-space: nowrap;
}

.role-select {
    width: 8rem;
    border-radius: 0.375rem;
}

.role-status {
    display: inline-flex;
    align-items: center;
    min-height: 1.625rem;
    border-radius: 999px;
    padding: 0 0.625rem;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
}

.role-status.is-enabled {
    background: hsl(var(--su) / 0.12);
    color: hsl(var(--su));
}

.role-status.is-muted {
    background: hsl(var(--bc) / 0.08);
    color: hsl(var(--bc) / 0.58);
}

.role-status.is-disabled {
    background: hsl(var(--er) / 0.12);
    color: hsl(var(--er));
}

.role-state {
    display: flex;
    min-height: 22rem;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: left;
}

.role-loading {
    padding: 0.5rem 1rem;
}

.role-loading__row {
    display: grid;
    height: 3.75rem;
    grid-template-columns: 2rem 10rem minmax(10rem, 1fr) 8rem;
    align-items: center;
    gap: 0.75rem;
    border-bottom: 1px solid hsl(var(--bc) / 0.08);
}

.role-skeleton {
    display: block;
    height: 0.75rem;
    border-radius: 0.25rem;
    background: hsl(var(--bc) / 0.08);
    animation: role-pulse 1.4s ease-in-out infinite;
}

.role-skeleton--avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
}

.role-skeleton--name { width: 7rem; }
.role-skeleton--email { width: min(14rem, 80%); }
.role-skeleton--select { width: 8rem; height: 2rem; }

.role-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.875rem;
}

@keyframes role-pulse {
    50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
    .role-skeleton { animation: none; }
}

@media (max-width: 640px) {
    .role-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .role-toolbar__actions {
        align-items: stretch;
        flex-direction: column;
    }

    .role-search { width: 100%; }
    .role-summary { flex-wrap: wrap; }
}
</style>
